import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Collapse, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ListAlt as TravauxIcon } from '@mui/icons-material';
import { useComptoirProductionTravaux } from '../../../hooks/queries/useComptoir';

const PRODUCTION_COLOR = '#FF5722';

const qty = (v: number | string) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

interface Props {
  type: 'projet' | 'service';
  id: number;
}

/**
 * The travaux of a Comptoir job, read-only and without prices, for the workshop:
 * a simple désignation is one line, a détaillée is its title then its lines 2.1, 2.2…
 * Renders nothing for a row that doesn't come from the Comptoir.
 */
const ComptoirTravauxTable: React.FC<Props> = ({ type, id }) => {
  const { data, isLoading } = useComptoirProductionTravaux(type, id);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} sx={{ color: PRODUCTION_COLOR }} /></Box>;
  }
  if (!data || data.designations.length === 0) return null;

  const cell = { py: 0.75, px: 1 };
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...cell, width: 44 }}>N°</TableCell>
          <TableCell sx={cell}>Désignation</TableCell>
          <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} align="right">Qté</TableCell>
          <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} align="right">Livré</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.designations.flatMap((d) => {
          const line = (l: typeof d.lignes[number], sub: boolean) => {
            const done = Number(l.livre) >= Number(l.quantite) && Number(l.quantite) > 0;
            return (
              <TableRow key={`${d.numero}-${l.numero}`}>
                <TableCell sx={{ ...cell, pl: sub ? 2.5 : 1, color: sub ? 'text.secondary' : undefined, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{l.numero}</TableCell>
                <TableCell sx={cell}>
                  <Typography variant="body2" sx={{ fontWeight: sub ? 500 : 600 }}>{l.designation}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{l.type_service}{l.description ? ` · ${l.description}` : ''}</Typography>
                </TableCell>
                <TableCell sx={{ ...cell, whiteSpace: 'nowrap', verticalAlign: 'top' }} align="right">{qty(l.quantite)} {l.unite}</TableCell>
                <TableCell sx={{ ...cell, whiteSpace: 'nowrap', verticalAlign: 'top', color: done ? 'success.main' : 'text.secondary', fontWeight: done ? 600 : undefined }} align="right">
                  {qty(l.livre)}
                </TableCell>
              </TableRow>
            );
          };
          if (!d.detaille) return [line(d.lignes[0], false)];
          return [
            <TableRow key={`g${d.numero}`} sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ ...cell, fontWeight: 700, verticalAlign: 'top' }}>{d.numero}</TableCell>
              <TableCell sx={cell} colSpan={3}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.libelle}</Typography>
                {d.description && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{d.description}</Typography>}
              </TableCell>
            </TableRow>,
            ...d.lignes.map((l) => line(l, true)),
          ];
        })}
      </TableBody>
    </Table>
  );
};

/** Suivi drawer: the same table behind a « Travaux demandés » header, folded by default so the
 *  phases stay in view. Renders nothing for a row that doesn't come from the Comptoir. */
export const ComptoirTravauxSection: React.FC<Props> = ({ type, id }) => {
  const [open, setOpen] = useState(false);
  const { data } = useComptoirProductionTravaux(type, id);
  if (!data || data.designations.length === 0) return null;
  return (
    <>
      <Divider />
      <ButtonBase onClick={() => setOpen((o) => !o)} sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 2, py: 1.25 }}>
        <TravauxIcon sx={{ fontSize: 18, color: PRODUCTION_COLOR }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Travaux demandés ({data.designations.length})</Typography>
        <Typography variant="caption" color="text.secondary">{data.offre_reference}</Typography>
        <ExpandMoreIcon sx={{ ml: 'auto', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1, pb: 1 }}>
          <ComptoirTravauxTable type={type} id={id} />
        </Box>
      </Collapse>
    </>
  );
};

export default ComptoirTravauxTable;
